import axios, { AxiosError } from "axios";

export interface AuthResponse {
  token: string;
  expiresAt: string;
  user: {
    id: string;
    username: string;
    role: string;
  };
}

// Token storage in memory
let authToken: string | null = null;
let tokenExpiration: string | null = null;

// Authentication state management
let loginInProgress = false;
let loginPromise: Promise<AuthResponse | null> | null = null;

// API client
export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_TIMESTAMP_API_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 10000,
});

// Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
      console.log("Added auth token to request");
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Clear token on 401 errors
    if (error.response?.status === 401) {
      authToken = null;
      tokenExpiration = null;
    }

    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  login: async (
    username?: string,
    password?: string
  ): Promise<AuthResponse> => {
    // If a login is already in progress, don't start another one
    if (loginInProgress) {
      console.log("Login already in progress, waiting for it to complete");
      if (loginPromise) {
        // Wait for the existing login to complete
        const result = await loginPromise;
        if (result) return result;
        // If the previous login returned null, continue with a new login
      }
    }

    try {
      console.log("Attempting login...");
      loginInProgress = true;

      // Create a new login promise
      loginPromise = (async () => {
        try {
          // Use provided credentials or environment variables
          const credentials = {
            username:
              username || process.env.REACT_APP_TIMESTAMP_API_USERNAME || "",
            password:
              password || process.env.REACT_APP_TIMESTAMP_API_PASSWORD || "",
          };

          console.log(`Using credentials for: ${credentials.username}`);

          // Get API URL from environment
          const apiUrl = process.env.REACT_APP_TIMESTAMP_API_URL;
          if (!apiUrl) {
            throw new Error("API URL is not configured");
          }

          // Make direct request to auth endpoint
          console.log(`Sending auth request to ${apiUrl}/auth/login`);
          const response = await axios.post<AuthResponse>(
            `${apiUrl}/auth/login`,
            credentials,
            { headers: { "Content-Type": "application/json" } }
          );

          // Store token in memory
          authToken = response.data.token;
          tokenExpiration = response.data.expiresAt;

          console.log("Login successful!");
          console.log("Token:", authToken);
          console.log("Expires at:", tokenExpiration);

          return response.data;
        } catch (error) {
          console.error("Login failed:", error);
          throw error;
        }
      })();

      // Wait for the login to complete
      const result = await loginPromise;
      if (!result) {
        throw new Error("Login failed: No response received");
      }
      return result;
    } catch (error) {
      throw error;
    } finally {
      loginInProgress = false;
      loginPromise = null;
    }
  },

  autoLogin: async (): Promise<AuthResponse | null> => {
    // Skip if already logged in
    if (authService.isAuthenticated()) {
      console.log("Already authenticated, skipping auto-login");
      return null;
    }

    const username = process.env.REACT_APP_TIMESTAMP_API_USERNAME;
    const password = process.env.REACT_APP_TIMESTAMP_API_PASSWORD;

    if (!username || !password) {
      console.warn("Auto-login failed: Missing credentials in environment");
      return null;
    }

    try {
      console.log("Attempting auto-login with environment credentials");
      // If login is already in progress, this will wait for it to complete
      return await authService.login(username, password);
    } catch (error) {
      // Don't fail the auto-login process if another login is in progress
      if (
        error instanceof Error &&
        error.message === "Login already in progress"
      ) {
        console.log(
          "Auto-login: Another login is in progress, will use that token when ready"
        );
        return null;
      }
      console.error("Auto-login failed:", error);
      return null;
    }
  },

  isAuthenticated: (): boolean => {
    if (!authToken || !tokenExpiration) {
      return false;
    }

    // Check if token is expired
    const now = new Date();
    const expires = new Date(tokenExpiration);
    return now < expires;
  },

  logout: (): void => {
    authToken = null;
    tokenExpiration = null;
  },

  getToken: (): string | null => {
    return authToken;
  },
};
